import * as React from 'react';
import { Logo, Icon, Link, Text } from 'ui';
import config from 'config';
import classNames from 'classnames';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import ApplicationMonitoring from 'framework/applicationMonitoring/ApplicationMonitoring';
import { GaActions } from 'framework/applicationMonitoring/interface';
import { featuresState } from 'stores/features';
import { view } from '@risingstack/react-easy-state';
import { prefixClassName } from '../ui/_conf';
import { NavigationSubNav } from './NavigationSubNav';
import { getRoutePath } from './service';
import './styles.scss';
import { useNavigation } from './UseNavigation';
import { useDisableBodyScroll } from './useDisableBodyScroll';
import { useMobileNav } from './MobileNavContext';

const version = config.buildNumber;
const navigationClass = prefixClassName('navigation');
const menuClass = prefixClassName('navmenu');

export const Navigation = view((props: RouteComponentProps) => {
  const [activeMenu, setActiveMenu] = React.useState(-1);
  const { authorisedModules } = useNavigation(featuresState);
  const { isMobile, isMobileNavOpen, closeMobileNav } = useMobileNav();
  const {
    location: { pathname = '' },
  } = props;

  const getFirstPathSegment = React.useCallback((pathname: string) => {
    const match = pathname.match(/^\/[^/]+/);
    return match ? match[0] : null;
  }, []);

  /** Disables all background scroll when mobile nav is open */
  useDisableBodyScroll(isMobile && isMobileNavOpen);

  /**
   * Auto-open the menu item that corresponds to the current path when mobile nav opens
   * If the current path is home ("/"), open the first menu item
   */
  React.useEffect(() => {
    if (isMobileNavOpen && isMobile && authorisedModules.length > 0) {
      const currentPath = getFirstPathSegment(pathname);
      setActiveMenu(currentPath ? authorisedModules.findIndex((mod) => mod.modulePath === currentPath) : 0);
    } else if (!isMobileNavOpen && isMobile) {
      setActiveMenu(-1);
    }
  }, [isMobileNavOpen, isMobile, authorisedModules.length]);

  const openSubMenu = (e: React.MouseEvent<Element, MouseEvent>, index: number) => {
    if (e) {
      e.preventDefault();
    }
    setActiveMenu(index);
  };

  const subMenuOpened = activeMenu > -1;
  const selectedAppModule = authorisedModules[activeMenu];

  return (
    <div
      className={classNames(navigationClass, {
        [`${navigationClass}--mobile-open`]: isMobileNavOpen && isMobile,
        [`${navigationClass}--mobile`]: isMobile,
      })}
    >
      <div className={`${navigationClass}__Container`}>
        <div className={`${navigationClass}__Main`}>
          <Logo {...(isMobile && isMobileNavOpen && { onClick: closeMobileNav })} />
          {authorisedModules.map((mod, i) => {
            const menuIsOpened = activeMenu === i;
            return (
              <Link
                key={i}
                className={classNames({
                  [menuClass]: true,
                  [`${menuClass}--opened`]: menuIsOpened,
                })}
                to={getRoutePath(mod.modulePath)}
                caption={mod.appMenuTitle}
                captionAfterChildren
                onClick={(e) => {
                  ApplicationMonitoring.trackEvent(pathname, GaActions.NavigationMenu, mod.appMenuTitle);
                  openSubMenu(e, menuIsOpened && !isMobile ? -1 : i);
                }}
              >
                <Icon name={mod.appMenuIcon as any} className={`${menuClass}__icon`} />
              </Link>
            );
          })}
          <div className={`${menuClass}__versionContainer`}>
            <Text tag="div" className={`${menuClass}__versionContainer--version`} caption="Version" />
            <Text tag="div" className={`${menuClass}__versionContainer--versionNumber`} caption={`${version}`} />
          </div>
        </div>
      </div>

      <NavigationSubNav
        appModule={selectedAppModule}
        isOpened={subMenuOpened}
        onSelectNav={isMobile ? closeMobileNav : (e) => openSubMenu(e, -1)}
      />

      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        onClick={isMobile ? closeMobileNav : (e) => openSubMenu(e, -1)}
        className={classNames(`${navigationClass}__overlay`, {
          [`${navigationClass}__overlay--opened`]: (isMobile && isMobileNavOpen) || (!isMobile && activeMenu > -1),
        })}
      />
    </div>
  );
});

export default withRouter(Navigation);
